import React, { useEffect, useState, useContext, SetStateAction } from 'react';
import { withStyles } from '@material-ui/core/styles';
import themeCreateInjectableStyles from 'Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import cn from 'classnames';
import ModifyingInfoModal from 'Components/EditOrder/comps/ModifyingInfoModal.component';
import {
  UI_SITE_CONTACT_PHONE_BLUR,
  UI_SITE_CONTACT_PHONE_FOCUS,
  UI_EDIT_ORDER_INFO_MODAL_OPEN,
  UI_EDIT_ORDER_MODAL_OPEN_TERMS,
} from 'State/Store/UI/UI.types';
import { Grid, Typography, FormControlLabel, Switch } from '@material-ui/core';
import { MFSDR } from 'Constants/MouseFlow/SensitiveDataReplacer';
import { Order } from 'types/Order';
import { useDispatch } from 'react-redux';
import uiTriggered from 'State/Store/UI/Actions/UI.Triggered.action';
import CustomPhoneInput from 'Components/CustomPhoneInput/CustomPhoneInput.component';
import HereMaps from 'Components/HereMaps/HereMaps.component';
import AccountProjectSelector from 'Components/PlaceOrder/AccountProjectSelector.component';
import DateTimeSelector from 'Components/PlaceOrder/DateTimeSelector.component';
import DriverInstructions from 'Components/PlaceOrder/DriverInstructions.component';
import LotNumberSelector from 'Components/PlaceOrder/LotNumberSelector.component';
import PlusLoad from 'Components/PlaceOrder/PlusLoad.component';
import PourMethodComponent from 'Components/PlaceOrder/PourMethod.component';
import ProductSelector from 'Components/PlaceOrder/ProductSelector.component';
import PurchaseOrderSelector from 'Components/PlaceOrder/PurchaseOrderSelector.component';
import QuantitySelector from 'Components/PlaceOrder/QuantitySelector.component';
import SlumpSelector from 'Components/PlaceOrder/SlumpSelector.component';
import TruckSizeSelector from 'Components/PlaceOrder/TruckSizeSelector.component';
import PageTip from 'Components/PageTip/PageTip.component';
import TruckSpacingSelector from 'Components/PlaceOrder/TruckSpacingSelector.component';
import { Nullable } from 'types/utils/Nullable';
import { resolveDateTimeOmittingTimeZoneDesignator, resolveDateTimeByReplacingDotWithColon } from '../utils/resolveDateTime';
import moment from 'moment';
import envGet from '../../../../Support/Env/Get/EnvGet.support';
import ServicesContext from 'Contexts/Services/Services.context';
import getFiltersSelectedAccount from 'State/Store/Filters/Selectors/FiltersSelectedAccount.selector';
import helpers from 'Support/Text';
import { connect } from 'react-redux';

const enableModifyingDate = envGet({ key: `REACT_APP_BORAL_FEATURE_TOGGLE_ENABLE_MODIFYING_DATE` }) === `true`;

export interface EditOrderForm {
  startTime: string;
  startDate: string;
  instructions: string;
  quantity: number;
  siteTel: string;
  isDateTimeCombinationValid?: boolean;
  purchaseOrder: string;
}

interface Props {
  classes: any;
  order: Order;
  orderForm: Nullable<EditOrderForm>;
  setSamePhoneNumber: Dispatch<SetStateAction<boolean>>;
  setPhoneNumberValid: Dispatch<SetStateAction<boolean>>;
  onFieldChange(param: { fieldKey: keyof EditOrderForm; fieldValue: any; isDateTimeCombinationValid?: boolean }): void;
  currentAccountGlobal: string;
  setPurchaseOrderRequired?: (value: boolean) => void;
}

const EditOrderStep2Comp = (props: Props) => {
  const { classes, order, orderForm, setSamePhoneNumber = () => {}, setPhoneNumberValid = () => {}, onFieldChange, currentAccountGlobal } = props;
  const dispatch = useDispatch();
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [accountProject, setAccountProject] = useState<{ account: any; project: any; isValid: boolean } | null>(null);

  if (!order.details) {
    return null;
  }
  const {
    siteTel,
    qty,
    instructions,
    projectName,
    po,
    deliveryAddress,
    lotNumber,
    truckSize,
    quantityAlreadyModified,
    slump,
    pourMethod,
    spacing,
    product,
    testingRequired,
    plusLoad,
  } = order.details;
  const { orderCode, startTime } = order.summary;
  const services = useContext(ServicesContext);

  let truckSpacing = qty.ordered || 0;

  if (spacing && typeof spacing === `string` && spacing.endsWith(` min`)) {
    truckSpacing = Number(spacing.replace(` min`, ``)) * 1;
  }

  useEffect(() => {
    const fetchAccountProject = async () => {
      try {
        const accounts = await services.orderService.getPlaceOrderAccounts();
        const foundAccount = accounts.find((acc: any) => acc.id.trim() === helpers.getNumberFromBillTo(currentAccountGlobal));
        if (!foundAccount) return;

        let selectedProject = null;
        if (foundAccount.projectRequired && foundAccount.projects.length > 0) {
          selectedProject = foundAccount.projects[0];
        }
        // Lift this up for EdirOrderModel to use for form validation based on account type
        const required = (selectedProject && selectedProject.purchaseOrderRequired) || (foundAccount && foundAccount.purchaseOrderRequired);
        props.setPurchaseOrderRequired?.(!!required);

        setAccountProject({
          account: foundAccount,
          project: selectedProject,
          isValid: true, // required to align with PlaceOrder shape
        });
      } catch (err) {
        console.error('Failed to fetch account/project info', err);
      }
    };

    fetchAccountProject();
  }, []);

  const phoneNumberHandler = (phoneNumber: string, hasError: boolean, isOriginalNumber: boolean) => {
    onFieldChange({
      fieldKey: 'siteTel',
      fieldValue: phoneNumber,
    });
    setSamePhoneNumber(isOriginalNumber);
    setPhoneNumberValid(!hasError);
  };

  const phoneBlurHandler = () => {
    dispatch(
      uiTriggered({
        type: UI_SITE_CONTACT_PHONE_BLUR,
      }),
    );
  };
  const phoneFocusHandler = () => {
    dispatch(
      uiTriggered({
        type: UI_SITE_CONTACT_PHONE_FOCUS,
      }),
    );
  };
  const onInfoModalOpen = () => {
    dispatch(
      uiTriggered({
        type: UI_EDIT_ORDER_INFO_MODAL_OPEN,
      }),
    );
    setInfoModalOpen(true);
  };
  const onInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  const dateTimeHandler = (upcomingItem: {
    isDateTimeCombinationValid: boolean;
    selectedDateTime: string;
    details: {
      day: number;
      hour: number;
      minute: number;
      month: number;
      year: number;
    };
  }) => {
    onFieldChange({
      fieldKey: 'startTime',
      fieldValue: moment(upcomingItem.selectedDateTime).format(),
      isDateTimeCombinationValid: upcomingItem.isDateTimeCombinationValid,
    });
  };

  const quantityHandler = (item: { isValid: boolean; quantity: string }) => {
    const { isValid, quantity } = item;
    onFieldChange({
      fieldKey: 'quantity',
      fieldValue: quantity,
    });
  };

  const driverInstructionsHandler = (item: { isTouched: boolean; isValid: boolean; selectedDriverInstructions: string }) => {
    const { isTouched, isValid, selectedDriverInstructions } = item;
    onFieldChange({
      fieldKey: 'instructions',
      fieldValue: selectedDriverInstructions,
    });
  };

  const purchaseOrderHandler = (item: { isValid: boolean; selectedPurchaseOrder: string }) => {
    const { selectedPurchaseOrder } = item;
    onFieldChange({
      fieldKey: 'purchaseOrder',
      fieldValue: selectedPurchaseOrder,
    });
  };

  const editTopContent = (
    <Grid container spacing={2} className={classes.editOrder}>
      <Grid item xs={12} className={cn(classes.marginTop3unit, classes.marginBottom2unit)}>
        <PageTip
          content={
            <React.Fragment>
              <Typography variant="body2" className={classes.toolTipContent}>
                Fields that cannot be modified at this time have been disabled.
              </Typography>
              <Typography variant="body2" onClick={onInfoModalOpen} className={cn(classes.textDecorationUnderline, classes.cursorPointer, classes.callBoral)}>
                {`Can't modify the field you want?`}
              </Typography>
            </React.Fragment>
          }
        />
      </Grid>
    </Grid>
  );
  const isRequiredPurchaseOrder =
    (accountProject?.project && accountProject.project.purchaseOrderRequired) || (accountProject?.account && accountProject.account.purchaseOrderRequired);
  console.log('isRequiredPurchaseOrder:', isRequiredPurchaseOrder);

  return !order ? null : (
    <React.Fragment>
      <ModifyingInfoModal shouldHideSwitch showOkBtnOnly open={infoModalOpen} onClose={onInfoModalClose} order={order} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {editTopContent}
          <hr className={classes.keyLine} />
          <Grid container spacing={2} className={classes.editOrder}>
            <Grid item xs={12}>
              <Typography variant="h6" className={classes.heading}>
                {`Account & Project`}
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.phoneInputItem} data-mf-replace-inner={MFSDR}>
              <CustomPhoneInput
                classHelperRoot={classes.marginTop0unit}
                classEditable={classes.touchedField}
                classTouched={classes.editableField}
                emptyValid
                fullWidth
                onBlur={phoneBlurHandler}
                onFocus={phoneFocusHandler}
                id="phoneNumber"
                label="SMS Notification Number"
                onPhoneNumberChange={(newId: any, newValue: any, errorText: any, isOriginalNumber: any) => {
                  phoneNumberHandler(newValue, errorText, isOriginalNumber);
                }}
                value={orderForm.siteTel}
                originalValue={order.details!.siteTel ? order.details!.siteTel : ''}
                helperText="This number will receive automated SMS’s for this order"
              />
            </Grid>
            <Grid item xs={12} data-mf-replace-inner={MFSDR}>
              <AccountProjectSelector isEdit disabled onValidChange={() => {}} defaultProject={{ name: projectName }} />
            </Grid>

            <Grid item xs={12} className={classes.marginBottom3unit} data-mf-replace-inner={MFSDR}>
              <PurchaseOrderSelector
                disabled={false}
                isRequired={isRequiredPurchaseOrder}
                defaultPurchaseOrder={{ selectedPurchaseOrder: po ? po.trim() : ``, isValid: true }}
                onChange={purchaseOrderHandler}
                classTouched={classes.touchedField}
                classEditable={classes.editableField}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" className={classes.heading}>
                {`Delivery Date & Time`}
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.marginBottom3unit}>
              <DateTimeSelector
                isEdit
                order={order}
                tradingRegion={order.details.tradingRegion}
                onChange={dateTimeHandler}
                dateDisabled={!enableModifyingDate}
                initialValue={orderForm.startTime}
                defaultDateTime={{
                  selectedDateTime: resolveDateTimeOmittingTimeZoneDesignator(resolveDateTimeByReplacingDotWithColon(order.summary.startTime)),
                  isValid: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" className={classes.heading}>
                Site Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <DriverInstructions
                isEdit
                originalValue={order.details.instructions ? order.details.instructions : ''}
                driverInstructionsContent={orderForm.instructions}
                onChange={driverInstructionsHandler}
              />
            </Grid>
            <Grid item xs={12} data-mf-replace-inner={MFSDR}>
              <HereMaps hideMap disabled defaultLocation={{ address: deliveryAddress }} />
            </Grid>
            <Grid item xs={12}>
              <LotNumberSelector disabled defaultLotNumber={{ selectedLotNumber: lotNumber }} />
            </Grid>
            <Grid item xs={12} className={classes.marginBottom3unit}>
              <TruckSizeSelector disabled defaultTruckSize={truckSize} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" className={classes.heading}>
                Concrete Settings
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <QuantitySelector
                disabled={quantityAlreadyModified}
                isEdit
                initialValue={orderForm.quantity}
                defaultQuantity={order.details ? order.details.qty.ordered : 0}
                onChange={quantityHandler}
                orderDateTime={resolveDateTimeOmittingTimeZoneDesignator(resolveDateTimeByReplacingDotWithColon(order.summary.startTime))}
              />
            </Grid>

            <Grid item xs={12}>
              <SlumpSelector disabled enteredSlump={slump ? Number(slump) : 0} />
            </Grid>

            <Grid item xs={12}>
              <PourMethodComponent isEdit defaultPourMethod={{ selectedPourMethod: { code: pourMethod } }} />
            </Grid>

            <Grid item xs={12}>
              <TruckSpacingSelector isEdit pourMethod={{ code: pourMethod }} defaultSpacing={truckSpacing} />
            </Grid>

            {product.map((product, i: number) => {
              return (
                <Grid item xs={12} key={product.code}>
                  <ProductSelector disabled defaultProduct={{ product: product.name, code: product.code.trim() }} />
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch disabled color="primary" checked={!!testingRequired} />}
                label={
                  <Typography variant="body2" className={classes.opacity05}>
                    Testing required
                  </Typography>
                }
              />

              <PlusLoad disabled isOn={plusLoad} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

const mapStateToProps = (state: any) => ({
  currentAccountGlobal: getFiltersSelectedAccount(state),
});

const ConnectedEditOrderStep2 = connect(mapStateToProps)(withStyles(themeCreateInjectableStyles() as any, { withTheme: true })(EditOrderStep2Comp));

export default ConnectedEditOrderStep2;
